import { SetlistData } from '../services/OptimalArtistSearch';

/**
 * Generate a deterministic hash for a playlist based on artist and setlist data
 * This allows us to identify if the same playlist has been created before
 */
export function generatePlaylistHash(artistMbid: string, setlistData: SetlistData): string {
  // Create a string that uniquely identifies this playlist
  const songNames = setlistData.songs
    .map(song => song.name.toLowerCase().trim())
    .join('|');
  
  const hashInput = `${artistMbid}:${songNames}`;
  
  // Use a simple hash function that handles Unicode characters
  return simpleHash(hashInput);
}

/**
 * Simple hash function that handles Unicode characters
 */
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36).substring(0, 16);
}

/**
 * Stored playlist information in localStorage
 */
export interface StoredPlaylist {
  playlistId: string;
  platform: 'apple' | 'spotify';
  createdAt: number;
  artistName: string;
  playlistName: string;
  playlistUrl?: string;
  shortUrl?: string;
}

/**
 * Playlist cache management
 */
export class PlaylistCache {
  private static readonly CACHE_KEY = 'created_playlists_v1';
  
  /**
   * Store a created playlist in cache
   */
  static store(hash: string, playlist: StoredPlaylist): void {
    try {
      const cache = this.getCache();
      cache[hash] = playlist;
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ Stored playlist in cache: ${playlist.playlistName}`);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ Failed to store playlist in cache:', error);
      }
    }
  }
  
  /**
   * Retrieve a playlist from cache
   */
  static get(hash: string): StoredPlaylist | null {
    try {
      const cache = this.getCache();
      return cache[hash] || null;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ Failed to retrieve playlist from cache:', error);
      }
      return null;
    }
  }
  
  /**
   * Check if a playlist exists in cache
   */
  static has(hash: string): boolean {
    return this.get(hash) !== null;
  }
  
  /**
   * Remove a playlist from cache
   */
  static remove(hash: string): void {
    try {
      const cache = this.getCache();
      delete cache[hash];
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ Failed to remove playlist from cache:', error);
      }
    }
  }
  
  /**
   * Get all cached playlists
   */
  static getAll(): Record<string, StoredPlaylist> {
    return this.getCache();
  }
  
  /**
   * Clear all cached playlists
   */
  static clear(): void {
    try {
      localStorage.removeItem(this.CACHE_KEY);
      if (process.env.NODE_ENV === 'development') {
        console.log('🧹 Cleared playlist cache');
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ Failed to clear playlist cache:', error);
      }
    }
  }
  
  
  /**
   * Get the cache object from localStorage
   */
  private static getCache(): Record<string, StoredPlaylist> {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      return cached ? JSON.parse(cached) : {};
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ Failed to parse playlist cache, resetting:', error);
      }
      return {};
    }
  }
}